import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { TrendingUp, TrendingDown, Bot } from "lucide-react";
import { useChartColors } from "@/features/dashboard/components/chartTheme";
import { useChartReveal } from "@/features/dashboard/components/motion";
import { CountUp } from "@/features/dashboard/components/CountUp";
import type { AutomationRate } from "../mockAnalytics";

interface AutomationRateCardProps {
  data: AutomationRate;
}

/** Donut showing AI-automated vs. human-escalated conversation share, with a center stat. */
export function AutomationRateCard({ data }: AutomationRateCardProps) {
  const colors = useChartColors();
  const { ref, revealed, isAnimationActive } = useChartReveal();
  const chartData = [
    { name: "AI Automated", value: data.automatedPct },
    { name: "Escalated to Human", value: data.escalatedPct },
  ];
  const TrendIcon = data.trendPct >= 0 ? TrendingUp : TrendingDown;

  return (
    <div className="flex h-64 flex-col items-center justify-center md:h-72">
      <div ref={ref} className="relative h-44 w-44 md:h-48 md:w-48">
        {revealed && (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={58}
                outerRadius={80}
                paddingAngle={4}
                dataKey="value"
                startAngle={90}
                endAngle={-270}
                isAnimationActive={isAnimationActive}
                animationDuration={800}
                animationEasing="ease-out"
                stroke="none"
              >
                <Cell fill={colors.primary} />
                <Cell fill="currentColor" className="text-slate-200 dark:text-slate-800" />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        )}
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <Bot className="mb-0.5 h-4 w-4 text-primary" />
          <span className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
            <CountUp value={data.automatedPct} suffix="%" />
          </span>
          <span className="text-[11px] text-muted-foreground">AI handled</span>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-1.5 text-xs font-medium">
        <span
          className={data.trendPct >= 0 ? "flex items-center text-success" : "flex items-center text-error"}
        >
          <TrendIcon className="h-3 w-3" />
          <span className="ml-1">{Math.abs(data.trendPct)}pt</span>
        </span>
        <span className="text-muted-foreground">vs. previous period</span>
      </div>
    </div>
  );
}
